import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Switch } from '../../components/ui/switch';
import { ArrowLeft, Video, Image as ImageIcon, Plus, Trash2, Upload, Pencil, Eye, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import { AdSenseSetup } from '../../components/AdSenseSetup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

export function AdminAdsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualAds, setManualAds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewAd, setPreviewAd] = useState(null);
  const fileInputRef = useRef(null);

  const emptyForm = {
    name: '',
    type: 'video',
    image_url: '',
    video_url: '',
    click_url: '',
    active: true
  };
  const [adForm, setAdForm] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const adsRes = await adminAPI.getManualAds();
      setManualAds(adsRes.data.ads || []);
    } catch (error) {
      toast.error('Failed to load ad configuration');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingAd(null);
    setAdForm(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (ad) => {
    setEditingAd(ad);
    setAdForm({
      name: ad.name || '',
      type: ad.type || 'banner',
      image_url: ad.image_url || '',
      video_url: ad.video_url || '',
      click_url: ad.click_url || '',
      active: ad.active !== false
    });
    setDialogOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');

    if (!isVideo && !isImage) {
      toast.error('Please upload an image or video file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error('File size must be under 100MB');
      return;
    }

    setUploading(true);
    try {
      const res = await adminAPI.uploadAdMedia(file);
      const url = res.data.url;

      if (isVideo) {
        setAdForm(prev => ({ ...prev, video_url: url, type: 'video' }));
      } else {
        setAdForm(prev => ({ ...prev, image_url: url, type: 'banner' }));
      }
      toast.success(`${isVideo ? 'Video' : 'Image'} uploaded successfully!`);
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmitAd = async (e) => {
    e.preventDefault();

    if (!adForm.name.trim()) {
      toast.error('Ad name is required');
      return;
    }
    if (adForm.type === 'video' && !adForm.video_url) {
      toast.error('Please upload or provide a video URL');
      return;
    }
    if (adForm.type === 'banner' && !adForm.image_url) {
      toast.error('Please upload or provide an image URL');
      return;
    }

    setSaving(true);
    try {
      if (editingAd) {
        await adminAPI.updateManualAd(editingAd._id, adForm);
        toast.success('Ad updated!');
      } else {
        await adminAPI.createManualAd(adForm);
        toast.success('Ad created!');
      }
      setDialogOpen(false);
      setAdForm(emptyForm);
      setEditingAd(null);
      loadData();
    } catch (error) {
      toast.error('Failed to save ad');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Delete this ad? This cannot be undone.')) return;
    try {
      await adminAPI.deleteManualAd(adId);
      toast.success('Ad deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const toggleAdStatus = async (ad) => {
    try {
      await adminAPI.updateManualAd(ad._id, { ...ad, active: !ad.active });
      toast.success(`Ad ${!ad.active ? 'activated' : 'deactivated'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update ad status');
    }
  };

  if (loading) return <LoadingSpinner className="py-20" />;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-ads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button onClick={() => navigate('/admin')} variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-white">Ad Management</h1>
        </div>
      </div>

      {/* Manual Ads Section */}
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">Rewarded Ads (your own ads)</h2>
            <p className="text-sm text-slate-400 mt-1">Your own video or image ads, shown before answers and results - on the website and in the Android/iOS apps</p>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
            data-testid="create-ad-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Ad
          </Button>
        </div>

        {manualAds.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-600 rounded-xl">
            <Video className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No ads yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first video or image ad</p>
            <Button onClick={openCreateDialog} className="mt-4 bg-teal-600 hover:bg-teal-700">
              <Plus className="w-4 h-4 mr-2" /> Create Ad
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {manualAds.map((ad) => (
              <div
                key={ad._id}
                className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                  ad.active
                    ? 'bg-slate-700/40 border-slate-600/50'
                    : 'bg-slate-800/30 border-slate-700/30 opacity-60'
                }`}
                data-testid={`ad-card-${ad._id}`}
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0 flex items-center justify-center">
                    {ad.type === 'video' && ad.video_url ? (
                      <video
                        src={resolveUrl(ad.video_url)}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                    ) : ad.image_url ? (
                      <img
                        src={resolveUrl(ad.image_url)}
                        alt={ad.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <Video className="w-6 h-6 text-slate-500" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-white truncate">{ad.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        ad.type === 'video' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {ad.type === 'video' ? 'Video' : 'Image'}
                      </span>
                      {ad.click_url && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Has link
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                  <Button
                    onClick={() => setPreviewAd(ad)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    data-testid={`preview-ad-${ad._id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => openEditDialog(ad)}
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    data-testid={`edit-ad-${ad._id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <button
                    onClick={() => toggleAdStatus(ad)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      ad.active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/40 text-slate-400'
                    }`}
                    data-testid={`toggle-ad-${ad._id}`}
                  >
                    {ad.active ? 'Active' : 'Inactive'}
                  </button>
                  <Button
                    onClick={() => handleDeleteAd(ad._id)}
                    variant="ghost"
                    size="sm"
                    className="text-rose-400 hover:text-rose-300"
                    data-testid={`delete-ad-${ad._id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Google AdSense: guided setup */}
      <AdSenseSetup />

      {/* Create/Edit Ad Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingAd ? 'Edit Ad' : 'Create New Ad'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitAd} className="space-y-4">
            <div>
              <Label className="text-slate-300">Ad Name</Label>
              <Input
                value={adForm.name}
                onChange={(e) => setAdForm({ ...adForm, name: e.target.value })}
                placeholder="e.g., Summer Sale Video"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
                data-testid="ad-name-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Ad Type</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setAdForm({ ...adForm, type: 'video' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                    adForm.type === 'video'
                      ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                      : 'bg-slate-700/30 border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  <Video className="w-4 h-4" /> Video
                </button>
                <button
                  type="button"
                  onClick={() => setAdForm({ ...adForm, type: 'banner' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                    adForm.type === 'banner'
                      ? 'bg-blue-500/20 border-blue-500/50 text-blue-300'
                      : 'bg-slate-700/30 border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  <ImageIcon className="w-4 h-4" /> Image
                </button>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-slate-300">Upload {adForm.type === 'video' ? 'Video' : 'Image'}</Label>
              <div
                className="mt-1 border-2 border-dashed border-slate-600 rounded-xl p-6 text-center cursor-pointer hover:border-teal-500/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={adForm.type === 'video' ? 'video/mp4,video/webm,video/ogg' : 'image/*'}
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="ad-file-input"
                />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <LoadingSpinner className="w-8 h-8" />
                    <p className="text-sm text-slate-400 mt-2">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-slate-500 mb-2" />
                    <p className="text-sm text-slate-400">
                      Click to upload {adForm.type === 'video' ? 'video (MP4, WebM)' : 'image (JPG, PNG)'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Max 100MB</p>
                  </div>
                )}
              </div>
            </div>

            {/* Preview uploaded media */}
            {adForm.type === 'video' && adForm.video_url && (
              <div className="rounded-lg overflow-hidden border border-slate-600 bg-black">
                <video
                  src={resolveUrl(adForm.video_url)}
                  controls
                  className="w-full max-h-48 object-contain"
                  preload="metadata"
                />
                <p className="text-xs text-slate-500 px-3 py-1 truncate">{adForm.video_url}</p>
              </div>
            )}
            {adForm.type === 'banner' && adForm.image_url && (
              <div className="rounded-lg overflow-hidden border border-slate-600 bg-black">
                <img
                  src={resolveUrl(adForm.image_url)}
                  alt="Preview"
                  className="w-full max-h-48 object-contain"
                />
                <p className="text-xs text-slate-500 px-3 py-1 truncate">{adForm.image_url}</p>
              </div>
            )}

            {/* Or paste URL */}
            <div>
              <Label className="text-slate-300">Or paste {adForm.type === 'video' ? 'Video' : 'Image'} URL</Label>
              <Input
                value={adForm.type === 'video' ? adForm.video_url : adForm.image_url}
                onChange={(e) => {
                  if (adForm.type === 'video') {
                    setAdForm({ ...adForm, video_url: e.target.value });
                  } else {
                    setAdForm({ ...adForm, image_url: e.target.value });
                  }
                }}
                placeholder={adForm.type === 'video' ? 'https://example.com/ad.mp4' : 'https://example.com/ad.jpg'}
                className="bg-slate-700/50 border-slate-600 text-white font-mono text-sm"
                data-testid="ad-url-input"
              />
            </div>

            <div>
              <Label className="text-slate-300">Click URL (optional)</Label>
              <Input
                value={adForm.click_url}
                onChange={(e) => setAdForm({ ...adForm, click_url: e.target.value })}
                placeholder="https://yoursite.com/promo"
                className="bg-slate-700/50 border-slate-600 text-white font-mono text-sm"
                data-testid="ad-click-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">Where users go when clicking the ad</p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={adForm.active}
                  onCheckedChange={(v) => setAdForm({ ...adForm, active: v })}
                />
                <Label className="text-slate-300">{adForm.active ? 'Active' : 'Inactive'}</Label>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="flex-1 text-slate-400"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
                data-testid="submit-ad-btn"
              >
                {saving ? 'Saving...' : editingAd ? 'Update Ad' : 'Create Ad'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      {previewAd && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewAd(null)}>
          <div className="bg-slate-800 rounded-2xl p-6 max-w-lg w-full border border-slate-700 relative" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewAd(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-white mb-4">Preview: {previewAd.name}</h3>
            <div className="rounded-xl overflow-hidden bg-black border border-slate-600">
              {previewAd.type === 'video' && previewAd.video_url ? (
                <video
                  src={resolveUrl(previewAd.video_url)}
                  controls
                  autoPlay
                  muted
                  className="w-full max-h-80 object-contain"
                />
              ) : previewAd.image_url ? (
                <img
                  src={resolveUrl(previewAd.image_url)}
                  alt={previewAd.name}
                  className="w-full max-h-80 object-contain"
                />
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-500">No media</div>
              )}
            </div>
            {previewAd.click_url && (
              <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> {previewAd.click_url}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
